import type { DailyReport, KpiMetrics, KpiTrendItem } from './api';
import { aggregateTrend } from './aggregate';
import { KPI_DEFINITIONS, type KpiId } from './kpi-definitions';

// ダッシュボード冒頭の「AI インサイト」を**規則で**組み立てる。
//
// ⚠️ **達成/未達の判定をここに書かないこと。** 目標値も方向（高いほど良い / 低いほど良い）も
// `kpi-definitions.ts` だけが持つ。ここは**その定義に照らして並べ替えて文にするだけ**にする。
// 判定を2箇所に置くと、タイルは「達成」でサマリーは「未達」という食い違いが黙って出る。
//
// 🔵 **API ではなく web に置いた理由。** 目標値は `kpi-definitions.ts` にしかなく、API 側は
// 持っていない。サマリーを API で作ると目標値を API にも写すことになり、**達成/未達を決める
// 場所が2つ**できる。`GET /dashboard/summary` は将来 Gemini 版を載せるために残してあり、
// あちらが本文を返したときはそちらを優先する（`generated_by` で区別が付く）。
//
// ⚠️ **未配線の指標は文に出さない。** 値が `null` の指標（満足度・AI完遂率）を
// 「0%」や「未計測」として並べると、配線漏れが「悪い数字」に見えてしまう。黙って外す。
//
// 🔵 文中は `label` ではなく **`metricName`**（短い指標名）を使う。カード見出しの
// `label` は「回答SLO ｜ 1分未満の実回答」のように説明込みで、文に並べると読めなくなる。

/** 1指標ぶんの評価結果。文を組み立てる前に、全指標をこの形に揃える */
/** @graph-connects none */
interface JudgedKpi {
  id: KpiId;
  name: string;
  value: number;
  /** 今期の運用目標。未設定なら null で、そのときは達成/未達を言わない */
  target: number | null;
  /** target があるときだけ入る */
  met: boolean | null;
  /** 目標に足りない量。`direction` を踏まえて**不足側を正**にする。達成していれば 0 以下 */
  shortfall: number | null;
}

/**
 * 懸念に本文付きで並べる未達の本数。4本とも並べると読む側が優先順位を付けられないので、
 * 遠い順に上位だけ出して残りは本数で添える。
 */
/** @graph-connects none */
const DETAILED_CONCERN_COUNT = 2;

/** 率の表示。少数1桁に揃える（タイル側と同じ桁数） */
/** @graph-connects none */
function pct(value: number, digits = 1): string {
  return `${(value * 100).toFixed(digits)}%`;
}

/** 差分の表示。符号を必ず付ける（「+0.2pt」と「0.2pt」では意味が変わる） */
/** @graph-connects none */
function signedPt(diff: number, digits = 1): string {
  const pt = diff * 100;
  return `${pt >= 0 ? '+' : ''}${pt.toFixed(digits)}pt`;
}

/** 件数の表示。率だけだと実数の動きが潰れるので、数えられるものは数で出す */
/** @graph-connects none */
function count(value: number): string {
  return `${Math.round(value).toLocaleString('ja-JP')}件`;
}

/** @graph-connects none */
function signedCount(diff: number): string {
  const rounded = Math.round(diff);
  return `${rounded >= 0 ? '+' : ''}${rounded.toLocaleString('ja-JP')}件`;
}

/** 月のラベル付きの点。前月・平均を「対象月より前」で切り出すために月を持つ */
/** @graph-connects none */
interface MonthPoint {
  /** 'YYYY-MM'。`KpiMetrics.month` と同じ形 */
  month: string;
  value: number;
}

/**
 * 確定月の系列を**古い順のまま**返す。
 * チャートと同じ `aggregateTrend` を通すので、**サマリーの数字はグラフと必ず一致する**。
 *
 * @graph-connects none
 */
function monthlyPoints(trend: KpiTrendItem[], metric: KpiId): MonthPoint[] {
  const points: MonthPoint[] = [];
  for (const point of aggregateTrend(trend, metric, 'month')) {
    if (point.value === null) continue;
    points.push({ month: point.period, value: point.value });
  }
  return points;
}

/** 末尾の値。`Array.prototype.at` は web 側の TS lib に入っておらず error 型になる */
/** @graph-connects none */
function lastValue(points: MonthPoint[]): number | null {
  const last = points[points.length - 1];
  return last === undefined ? null : last.value;
}

/**
 * 対象月**より前**の点だけ返す。
 *
 * ⚠️ 末尾から数えて切ってはいけない。系列は対象月より先まで点を持つことがあり
 * （退会率は集計途中の当月にも点が立つ）、`length - 2` を前月とみなすと
 * **対象月そのものを前月として引いてしまい、前月比が +0.0pt になる**。
 * 画面上は「横ばい」に見えるので、間違っていることに気づけない。
 *
 * @graph-connects none
 */
function monthsBefore(points: MonthPoint[], month: string): MonthPoint[] {
  const index = points.findIndex((point) => point.month === month);
  // 対象月が系列に無いこともある（確定前に取り込みが止まった等）。
  // 'YYYY-MM' は辞書順＝時系列順なので、そのまま比較で切れる
  return index < 0 ? points.filter((point) => point.month < month) : points.slice(0, index);
}

/**
 * 定義に照らして1指標を評価する。**判定式はここに書かず、定義の `target` と `direction` だけを見る。**
 *
 * @graph-connects none
 */
function judge(id: KpiId, value: number | null): JudgedKpi | null {
  if (value === null) return null;
  const def = KPI_DEFINITIONS[id];
  if (def.target === null) {
    return { id, name: def.metricName, value, target: null, met: null, shortfall: null };
  }
  // direction='up' は高いほど良い。不足側を正にすると、どちらの向きでも
  // 「shortfall が大きい順＝目標から遠い順」で並べ替えられる
  const shortfall = def.direction === 'up' ? def.target - value : value - def.target;
  return { id, name: def.metricName, value, target: def.target, met: shortfall <= 0, shortfall };
}

/**
 * CS の KPI4本。**この並びが文中の並び順**（定義順）になる。
 * モニタリングの2本（満足度・AI完遂率）は判定対象ではないので含めない。
 */
/** @graph-connects none */
const CS_KPI_IDS: KpiId[] = [
  'firstResponseSlaRate',
  'answerSlaRate',
  'threeTurnRate',
  'resolutionRate',
];

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business KPIの確定値と月次系列から、ダッシュボードのAIインサイト本文を規則ベースで組み立てる
 * @graph-connects aggregateTrend [calls] チャートと同じ月次系列を得る
 */
export function buildRuleSummary(
  kpi: KpiMetrics | null,
  trend: KpiTrendItem[],
): DailyReport | null {
  // 対象月が無いときは「データが無い」が正しい。空文字のカードを出すとデータがあるように見える
  if (kpi === null) return null;

  // ⚠️ 当月の値は系列からではなく `kpi` から取る。系列は確定月より先まで点を持つことがあり、
  // 末尾を取るとヒーローのタイルと違う月を語ってしまう
  const churnRate = kpi.kgi.churnRate;
  const churnCount = kpi.crm.churnCount;
  const pastRates = monthsBefore(monthlyPoints(trend, 'churnRate'), kpi.month);
  const pastCounts = monthsBefore(monthlyPoints(trend, 'churnCount'), kpi.month);
  const prevChurnRate = lastValue(pastRates);
  const prevChurnCount = lastValue(pastCounts);

  // 3ヶ月に満たない平均は出さない。1ヶ月だと「前月」をもう一度言っているだけで、
  // 2ヶ月でも当月と見比べる意味がほとんど無い
  const past = pastRates.slice(-12);
  const average =
    past.length >= 3 ? past.reduce((sum, point) => sum + point.value, 0) / past.length : null;

  // 定義順で評価しておき、並べ替えが要る所（見出し・懸念）でだけ遠い順に直す
  const csJudged = CS_KPI_IDS.map((id) =>
    judge(id, kpi.cs[id as keyof typeof kpi.cs] ?? null),
  ).filter((j): j is JudgedKpi => j !== null);
  const missed = csJudged
    .filter((j) => j.met === false)
    .sort((a, b) => (b.shortfall ?? 0) - (a.shortfall ?? 0));

  return {
    report_date: kpi.date,
    headline: buildHeadline(kpi.month, churnRate, prevChurnRate, csJudged, missed),
    body: buildBody(kpi, churnRate, prevChurnRate, average, past.length, csJudged),
    highlights: buildHighlights(churnCount, prevChurnCount, csJudged),
    concerns: buildConcerns(kpi, churnRate, average, churnCount, prevChurnCount, missed),
    generated_by: 'rule',
  };
}

/** @graph-connects none */
function buildHeadline(
  month: string,
  churnRate: number | null,
  prevChurnRate: number | null,
  csJudged: JudgedKpi[],
  missed: JudgedKpi[],
): string {
  const parts: string[] = [];
  if (churnRate !== null) {
    const diff = prevChurnRate !== null ? `（前月比 ${signedPt(churnRate - prevChurnRate)}）` : '';
    parts.push(`${month} の退会率は ${pct(churnRate)}${diff}。`);
  }
  if (missed.length > 0) {
    const worst = missed[0];
    // 全本数が未達のときに「4本のうち4本」と書くと回りくどいので言い分ける
    const scope =
      missed.length === csJudged.length
        ? `配線済み${String(csJudged.length)}本とも目標未達`
        : `配線済み${String(csJudged.length)}本のうち${String(missed.length)}本が目標未達`;
    parts.push(
      `CS は${scope}で、最も遠いのは${worst.name} ${pct(worst.value)}（${signedPt(-(worst.shortfall ?? 0))}）。`,
    );
  } else if (csJudged.length > 0) {
    parts.push(`CS は配線済み${String(csJudged.length)}本とも目標を満たしている。`);
  }
  // 退会率も CS も無いときは、空文字より「確定した指標が無い」と言う方が誤解がない
  return parts.length > 0 ? parts.join('') : `${month} は確定した指標がまだない。`;
}

/** @graph-connects none */
function buildBody(
  kpi: KpiMetrics,
  churnRate: number | null,
  prevChurnRate: number | null,
  average: number | null,
  averageMonths: number,
  csJudged: JudgedKpi[],
): string {
  const sentences: string[] = [];

  if (churnRate !== null) {
    const prev = prevChurnRate !== null ? `（前月 ${pct(prevChurnRate)}）` : '';
    // 平均との上下は「良い/悪い」と言い切らない。季節性があり1ヶ月では判断できない
    const vsAverage =
      average !== null
        ? `当月を除く直近${String(averageMonths)}ヶ月の平均 ${pct(average)} を${churnRate > average ? '上回る' : '下回る'}。`
        : '';
    sentences.push(`当月の退会率は ${pct(churnRate)}${prev}。${vsAverage}`);
  }

  const onboarding = kpi.crm.onboardingRate;
  const loyalty = kpi.crm.loyaltyRate;
  if (onboarding !== null || loyalty !== null) {
    const items: string[] = [];
    // 登録週コホートなので、どの週の話かを必ず添える（確定までのラグが指標ごとに違う）
    if (onboarding !== null) {
      const week = kpi.crm.onboardingCohortWeek;
      items.push(
        `${KPI_DEFINITIONS.onboardingRate.metricName} ${pct(onboarding)}${week === null ? '' : `（登録週 ${week}）`}`,
      );
    }
    if (loyalty !== null) {
      const week = kpi.crm.loyaltyCohortWeek;
      items.push(
        `${KPI_DEFINITIONS.loyaltyRate.metricName} ${pct(loyalty)}${week === null ? '' : `（登録週 ${week}）`}`,
      );
    }
    sentences.push(`${items.join('、')}。`);
  }

  if (csJudged.length > 0) {
    sentences.push(`CS は ${csJudged.map((j) => `${j.name} ${pct(j.value)}`).join('、')}。`);
  }

  return sentences.join('');
}

/** @graph-connects none */
function buildHighlights(
  churnCount: number | null,
  prevChurnCount: number | null,
  csJudged: JudgedKpi[],
): string[] {
  const highlights: string[] = [];

  // 率より先に実数を出す。率の小さな動きは実数の大きな変化を潰す
  if (churnCount !== null && prevChurnCount !== null && churnCount < prevChurnCount) {
    highlights.push(
      `${KPI_DEFINITIONS.churnCount.metricName} ${count(churnCount)}（前月比 ${signedCount(churnCount - prevChurnCount)}）`,
    );
  }

  for (const judged of csJudged) {
    if (judged.met === true) {
      highlights.push(
        `${judged.name} ${pct(judged.value)}。目標 ${pct(judged.target ?? 0, 0)} を達成`,
      );
    }
  }

  return highlights;
}

/** @graph-connects none */
function buildConcerns(
  kpi: KpiMetrics,
  churnRate: number | null,
  average: number | null,
  churnCount: number | null,
  prevChurnCount: number | null,
  missed: JudgedKpi[],
): string[] {
  const concerns: string[] = [];

  if (churnRate !== null && average !== null && churnRate > average) {
    concerns.push(
      `${KPI_DEFINITIONS.churnRate.metricName} ${pct(churnRate)} が当月を除く直近の平均 ${pct(average)} を上回っている`,
    );
  }

  if (churnCount !== null && prevChurnCount !== null && churnCount > prevChurnCount) {
    concerns.push(
      `${KPI_DEFINITIONS.churnCount.metricName} ${count(churnCount)}（前月比 ${signedCount(churnCount - prevChurnCount)}）`,
    );
  }

  for (const judged of missed.slice(0, DETAILED_CONCERN_COUNT)) {
    concerns.push(
      `${judged.name} ${pct(judged.value)}。目標 ${pct(judged.target ?? 0, 0)} に ${signedPt(-(judged.shortfall ?? 0))}`,
    );
  }
  const rest = missed.slice(DETAILED_CONCERN_COUNT);
  if (rest.length > 0) {
    concerns.push(
      `ほかに${String(rest.length)}本が目標未達（${rest.map((j) => j.name).join('、')}）`,
    );
  }

  // ⚠️ 初動SLO と回答SLO は単位も母集団も違うので引き算しない。
  //    差を出してよいのは分母が同じ解決率系2本だけで、その差は
  //    「4ターム以上かかったが解決」の層。「3ターム以内だが未解決」は含まない
  const threeTurn = kpi.cs.threeTurnRate;
  const resolution = kpi.cs.resolutionRate;
  if (threeTurn !== null && resolution !== null && resolution > threeTurn) {
    concerns.push(
      `${KPI_DEFINITIONS.threeTurnRate.metricName}との差 ${signedPt(resolution - threeTurn)} が「解決したが4ターム以上かかった」層`,
    );
  }

  return concerns;
}
